import * as React from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { graphql, StaticQuery } from 'gatsby';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { LocationLanguageContextType, StrapiTemplateSearchResult, StrapiSearchResult } from '../typings';
import ResultCard from './ResultCard';
import ResultCardMobile from './ResultCardMobile';
import { Languages } from '../utils/getAllSupportedLanguages';

interface SearchResultQueryProps {
  allStrapiSearchResult: {
    nodes: StrapiSearchResult[];
  };
}

interface SearchResultProps {
  searchResults: StrapiTemplateSearchResult[];
  isMobile: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    searchResultContainer: {
      display: 'grid',
      gap: '1rem',
      marginTop: '1rem',
      gridTemplateColumns: '100%'
    },
    paginationContainer: {
      display: 'grid',
      placeItems: 'center'
    }
  })
);

const SearchResultTable: React.FC<SearchResultProps> = ({ searchResults, isMobile }) => (
  <StaticQuery
    query={graphql`
      query SearchResultQuery {
        allStrapiSearchResult {
          nodes {
            PluralEntryLabel
            SingularEntryLabel
            locale
          }
        }
      }
    `}
    render={(data: SearchResultQueryProps) => {
      const classes = useStyles();
      const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
      const language: string = locationLanguage?.language ?? Languages.English;

      const searchResultContent = data.allStrapiSearchResult.nodes.find((x: any) => x.locale === language);
      const MAX_RESULT_PER_PAGE = 20;
      const NUM_PAGE = Math.ceil(searchResults.length / MAX_RESULT_PER_PAGE);
      const [page, setPage] = React.useState(1);
      const handleChange = (_: React.ChangeEvent<unknown>, value: number) => {
        window.scrollTo(0, 0);
        setPage(value);
      };

      // Reset to the first page when search result changed
      React.useEffect(() => {
        window.scrollTo(0, 0);
        setPage(1);
      }, [NUM_PAGE]);
      if (searchResultContent) {
        return (
          <>
            <Typography variant='body1'>
              {searchResults.length <= 1
                ? `${searchResults.length} ${searchResultContent.SingularEntryLabel}`
                : `${searchResults.length} ${searchResultContent.PluralEntryLabel}`}
            </Typography>
            <div className={classes.searchResultContainer}>
              {searchResults.map((eachResult, i) => {
                if (i >= (page - 1) * MAX_RESULT_PER_PAGE && i < page * MAX_RESULT_PER_PAGE) {
                  if (isMobile) return <ResultCardMobile resultDetails={eachResult} key={eachResult.Slug} />;
                  return <ResultCard resultDetails={eachResult} key={eachResult.Slug} />;
                }
                return null;
              })}
              {searchResults.length !== 0 && (
                <div className={classes.paginationContainer}>
                  <Pagination count={NUM_PAGE} page={page} onChange={handleChange} color='primary' />
                </div>
              )}
            </div>
          </>
        );
      }
      return null;
    }}
  />
);

export default SearchResultTable;
